import React, { useEffect, useState } from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useUserActions } from "store";
import { AsyncContent } from "components";
import { AltiusErrors, IUserModule } from "store/types";
import { useModals } from "hooks";

type GPTPromptType = Required<IUserModule>["gptPrompt"];

const Content = ({ initialPrompt }: { initialPrompt: GPTPromptType }) => {
  const queryClient = useQueryClient();
  const { updateUserGptSettings, deleteUserGptSettings } = useUserActions();
  const [prompt, setPrompt] = useState(initialPrompt);
  const { successModal, errorModal } = useModals();

  useEffect(() => {
    setPrompt(initialPrompt);
  }, [initialPrompt]);

  const { mutate } = useMutation<void, AltiusErrors, GPTPromptType>((data) => updateUserGptSettings(data), {
    onSuccess: () => {
      successModal({ msg: "Prompt changed" });
      queryClient.invalidateQueries(["user-settings"]);
    },
    onError: (error) => errorModal({ msg: error.message })
  });

  const { mutate: toDefaultMutate } = useMutation<void, AltiusErrors, void>(() => deleteUserGptSettings(), {
    onSuccess: () => queryClient.invalidateQueries(["user-settings"])
  });

  const isPlaceholder = (content: string) => ["__pdf__", "__example-pdf__", "__values__"].includes(content);

  return (
    <Stack width={800} sx={{ height: "100%" }} gap={10}>
      <Stack gap={10} overflow="auto" height="80%" paddingTop="20px">
        {prompt.map(({ content, role }, index) => (
          <TextField
            key={index}
            multiline
            rows={4}
            label={role.charAt(0).toUpperCase() + role.slice(1) + " Prompt"}
            value={content}
            disabled={isPlaceholder(content)}
            onChange={(e) =>
              setPrompt([...prompt.slice(0, index), { role, content: e.target.value }, ...prompt.slice(index + 1)])
            }
            InputProps={{ sx: { backgroundColor: "white" } }}
          />
        ))}
      </Stack>
      <Typography variant="body2">
        Note: in order for the GPT result to be considered valid, it must return a json array of items, each containing
        the fields: <b>polygon_id, text_value, date</b> and <b>category</b>. Otherwise, the system will not be able to
        use read the result and will return a failure.
      </Typography>
      <Stack direction="row" alignSelf="flex-end" gap={5}>
        <Button variant="outlined" onClick={() => toDefaultMutate()}>
          Reset to default (irreversible)
        </Button>
        <Button variant="contained" onClick={() => mutate(prompt)}>
          Update
        </Button>
      </Stack>
    </Stack>
  );
};

export const GPTPrompt = () => {
  const { getUserGptSettings } = useUserActions();

  const { data, isLoading, error } = useQuery(["user-settings"], getUserGptSettings);

  return (
    <AsyncContent loading={isLoading}>
      {!error && data && <Content initialPrompt={data} />}
      {(error as string) && <Typography>Error while fetching GPT settings</Typography>}
    </AsyncContent>
  );
};
